import React, { useRef } from 'react'
import { createUseStyles } from 'react-jss'
import cn from 'classnames'
import isEmpty from 'lodash/isEmpty'
import ResponsiveImage from '../ResponsiveImage'
import theme from '../../style/theme'
import { span } from '../../style/span'
import { vw } from '../../style/vw'
import Link from '../Link'
import detectIt from 'detect-it'
import { useHoverCursor } from '../Cursor'

const Tile = ({ className, title, image, subTitle, link, size }) => {
  const classes = useStyles()
  const ref = useRef()
  useHoverCursor(ref, !isEmpty(link))

  // Add better debugging for image format
  // console.log('Tile component received image:', image, 'for title:', title)
  // Enhanced image processing with support for multiple formats
  let processedImage = image

  if (typeof image === 'string') {
    // Convert string URL to image object
    const imageUrl = image.startsWith('http') ? image : `https:${image}`
    processedImage = {
      src: imageUrl,
      alt: title || 'Recipe image',
      sizes: [{
        url: imageUrl,
        width: 800
      }]
    }
  } else if (image && image.src && !image.sizes) {
    // Convert simple src to sizes array
    const imageUrl = image.src.startsWith('http') ? image.src : `https:${image.src}`
    processedImage = {
      ...image,
      src: imageUrl,
      sizes: [{
        url: imageUrl,
        width: 800
      }]
    }
  } else if (image && image.asset && image.asset._ref) {
    // Handle Sanity image format with _ref - keep but add a src as backup
    processedImage = {
      ...image,
      alt: title || 'Recipe image',
      // Use a data URL that can be rendered by the browser
      src: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII='
    }
  }

  // Enhanced image handling with debugging
  const imageIsValid = processedImage && (
    (processedImage.sizes && processedImage.sizes.length > 0) ||
    processedImage.src ||
    (processedImage.asset && processedImage.asset._ref)
  )
  // console.log('Image is valid:', imageIsValid, processedImage)

  return (
    <Link className={cn(classes.tile, { withLink: !isEmpty(link) }, size)} nonLinkTag='div' link={link} ref={ref}>
      <div>
        {imageIsValid ? (
          <div className={classes.imageContainer}>
            <ResponsiveImage {...processedImage} className={classes.image} aspect={435 / 590} />
          </div>
        ) : (
          <div className={classes.placeholderImage}>
            <div className={classes.imagePlaceholder}>Recipe Image</div>
          </div>
        )}
        <div className={classes.content}>
          {title && <div className={classes.title}>{title}</div>}
        </div>
      </div>
    </Link>
  )
}

const useStyles = createUseStyles({
  tile: {
    display: 'block',
    whiteSpace: 'normal',
    color: theme.colors.textSecondary,
    width: vw(290),
    position: 'relative',
    margin: [0, span(1), 0, 0],
    transform: 'rotate(0deg)', // No rotation for clean design
    overflow: 'hidden', // Ensure image corners are rounded
    [theme.breakpoints.up('md')]: {
      width: span(7.85, 'md'),
      margin: [0, span(0.18, 'md'), 0, span(0.18, 'md')] // Adjust margin for better spacing
    },
    ...(detectIt.primaryInput === 'touch' ? {} : {
      '&.withLink': {
        '& $image img': {
          transition: 'transform 1.6s ease-in-out'
        },
        '&:hover $image img': {
          transform: 'scale(1.1)'
        }
      }
    }),
    '&.large': {
      [theme.breakpoints.up('md')]: {
        width: span(7.85, 'md')
      }
    }
  },
  imageContainer: {
    overflow: 'hidden', // Ensure image hover effects don't overflow
    position: 'relative',
    width: '100%',
    background: '#f5f5f5', // Background color for images with transparency
    // Add better styling for images
    '& .ResponsiveImage-container-0-2-100': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      '& img': {
        objectFit: 'cover',
        width: '100%',
        height: '100%'
      }
    }
  },
  image: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    '& span': {
      transform: 'translateZ(0)'
    }
  },
  placeholderImage: {
    width: '100%',
    height: 0,
    paddingBottom: '135.6%', // 435/590 aspect ratio (same as ResponsiveImage)
    background: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    marginBottom: '12px'
  },
  imagePlaceholder: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#aaa',
    fontFamily: theme.fonts.body,
    fontSize: '16px',
    textAlign: 'center',
    width: '100%'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    transform: 'translateZ(0)',
    minHeight: '60px'
  },
  title: {
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    textAlign: 'left',
    color: theme.colors.primary, // Using primary color for better visibility
    fontSize: vw(16),
    lineHeight: 1.3,
    marginTop: '10px',
    [theme.breakpoints.up('md')]: {
      fontSize: vw(24, 'desktop')
    }
  },
  // Keeping the subtitle style for reference but it's no longer displayed
  subTitle: {
    fontSize: vw(14),
    fontFamily: theme.fonts.body,
    textAlign: 'left',
    display: 'none', // Hide subtitle as per requirements
    [theme.breakpoints.up('md')]: {
      fontSize: vw(16, 'desktop')
    }
  }
}, { name: 'Tile' })

export default Tile
